'use client';

import * as sx from '@stylexjs/stylex';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import React from 'react';
import { DayPicker } from 'react-day-picker';

import { globalTokens as $, color, type } from '@/app/tokens.stylex';

import type { Language } from '@/lib/constants';
import type { Locale } from 'date-fns';

type Locales = {
  [Key in Exclude<Language, 'en'>]: () => Promise<unknown>;
};

export const locales: Locales = {
  es: () => import('date-fns/locale/es'),
  el: () => import('date-fns/locale/el'),
  it: () => import('date-fns/locale/it'),
  fr: () => import('date-fns/locale/fr'),
  ca: () => import('date-fns/locale/ca'),
  sv: () => import('date-fns/locale/sv'),
};

const BORDER_WIDTH = '1px';
const s = sx.create({
  wrapper: {
    borderColor: {
      default: `rgb(${color.borderDim})`,
      ':focus-within': `rgb(${color.border})`,
      // eslint-disable-next-line @stylexjs/valid-styles
      ':has([aria-selected="true"])': {
        default: `rgb(${color.accentATint})`,
        ':focus-within': `rgb(${color.accentATint})`,
        ':hover': `rgb(${color.accentATint})`,
      },
      ':hover': `rgb(${color.border})`,
    },
    borderRadius: $.space,
    borderStyle: 'solid',
    borderWidth: BORDER_WIDTH,
    marginBottom: `calc(${$.space} * 1)`,
    marginTop: `calc(${$.space} * 3)`,
    maxWidth: '500px',
    paddingBlock: `calc(${$.space} * 2 - ${BORDER_WIDTH})`,
    paddingInline: `calc(${$.space} * 4 - ${BORDER_WIDTH})`,
    transition: 'border-color 0.25s',
  },
  vhidden: {
    borderWidth: '0',
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: '1px',
    margin: '0 -1px -1px 0',
    opacity: 0,
    overflow: 'hidden',
    padding: '0',
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
    wordWrap: 'normal',
  },
  btnReset: {
    cursor: 'default',
    outline: {
      default: 'none',
      ':focus-visible': 'none',
    },
  },
  btn: {
    alignItems: 'center',
    aspectRatio: '1/1',
    borderRadius: '50%',
    display: 'inline-flex',
    justifyContent: 'center',
    transition: 'all 0.25s',
    width: '2em',
  },
  caption: {
    alignItems: 'center',
    borderBottomColor: `rgba(${color.borderDim},0.3)`,
    borderBottomStyle: 'solid',
    borderBottomWidth: BORDER_WIDTH,
    display: 'flex',
    justifyContent: 'center',
    marginBottom: `calc(${$.space} * 3)`,
    paddingBottom: `calc(${$.space} * 3 - ${BORDER_WIDTH})`,
    paddingTop: `calc(${$.space} * 2)`,
    position: 'relative',
  },
  // caption_label: "text-sm font-medium",
  // nav: "space-x-1 flex items-center",
  nav_button: {
    backgroundColor: {
      default: null,
      ':hover': `rgba(${color.accentA},0.15)`,
      ':focus': `rgba(${color.accentA},0.15)`,
    },
    cursor: 'pointer',
    opacity: {
      default: 0.5,
      ':hover': 1,
      ':focus': 1,
    },
    position: 'absolute',
    top: `calc(${$.space} * 1)`,
  },
  nav_button_previous: {
    insetInlineStart: '0px',
  },
  nav_button_next: {
    insetInlineEnd: '0px',
  },
  table: {
    borderCollapse: 'collapse',
    fontSize: type.fzMin1,
    lineHeight: type.lhMin1,
    maxWidth: `calc(${$.space} * 125)`,
    tableLayout: 'fixed',
    width: '100%',
  },
  // head_row: "flex",
  head_cell: {
    color: `rgb(${color.textXDim})`,
    fontWeight: 400,
    paddingBottom: `calc(${$.space} * 2)`,
  },
  cell: {
    textAlign: 'center',
  },
  day: {
    backgroundColor: {
      default: null,
      // eslint-disable-next-line @stylexjs/valid-styles
      ':not([disabled]):not([aria-selected])': {
        default: null,
        ':hover': `rgba(${color.accentA},0.15)`,
        ':focus': `rgba(${color.accentA},0.15)`,
      },
    },
    cursor: {
      default: null,
      ':not([disabled])': 'pointer',
    },
  },
  day_selected: {
    backgroundColor: `rgb(${color.accentA})`,
    color: `rgb(${color.textInverse})`,
  },
  day_today: {
    fontWeight: 700,
  },
  day_outside: {
    color: `rgba(${color.textXDim},0.7)`,
  },
  day_disabled: {
    opacity: 0.25,
  },
  day_hidden: {
    visibility: 'hidden',
  },
});

const july2024 = new Date(2024, 6);
const july10th2024 = new Date(2024, 6, 10);
const now = new Date();
const isBeforeJuly2024 = now < july2024;
const initialMonth = isBeforeJuly2024 ? july2024 : now;

export default function Calendar({ lang }: { lang?: Language }) {
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>();
  const [locale, setLocale] = React.useState<Locale | undefined>(undefined);

  const date = selectedDate?.getDate();
  const month = selectedDate ? selectedDate.getMonth() + 1 : undefined;
  const year = selectedDate?.getFullYear();

  React.useEffect(() => {
    async function importLocale() {
      if (lang && lang !== 'en') {
        const { default: localeToSet } = (await locales[lang]()) as { default: Locale };
        setLocale(localeToSet);
      }
    }
    importLocale();
  }, [lang]);

  return (
    <>
      <DayPicker
        locale={locale}
        disabled={isDisabled}
        defaultMonth={initialMonth}
        fromMonth={initialMonth}
        mode="single"
        onSelect={setSelectedDate}
        selected={selectedDate}
        showOutsideDays
        className={sx.props(s.wrapper).className}
        classNames={{
          vhidden: sx.props(s.vhidden).className,
          button_reset: sx.props(s.btnReset).className,
          button: sx.props(s.btn).className,
          // months: sx.props(s.months).className,
          caption: sx.props(s.caption).className,
          nav_button: sx.props(s.nav_button).className,
          nav_button_previous: sx.props(s.nav_button_previous).className,
          nav_button_next: sx.props(s.nav_button_next).className,
          table: sx.props(s.table).className,
          head_cell: sx.props(s.head_cell).className,
          cell: sx.props(s.cell).className,
          day: sx.props(s.day).className,
          day_selected: sx.props(s.day_selected).className,
          day_today: sx.props(s.day_today).className,
          day_outside: sx.props(s.day_outside).className,
          day_disabled: sx.props(s.day_disabled).className,
          day_hidden: sx.props(s.day_hidden).className,
        }}
      />
      <input type="hidden" id="arrivalDay" name="arrivalDay" value={date} />
      <input type="hidden" id="arrivalMonth" name="arrivalMonth" value={month} />
      <input type="hidden" id="arrivalYear" name="arrivalYear" value={year} />
    </>
  );
}

function isDisabled(cellDate: Date) {
  if (cellDate < now) return true;

  const isWednesday = cellDate.getDay() === 3;
  const isOctober9th2024 =
    cellDate.getDate() === 9 && cellDate.getMonth() === 9 && cellDate.getFullYear() === 2024;

  const isOtherWednesday =
    isWednesday && differenceInCalendarDays(july10th2024, cellDate) % 14 === 0;

  return !isOtherWednesday && !isOctober9th2024;
}
